import { KeyboardNavigator } from '@bn-games/input-core';
import { BOARD_SIZE, squareAt } from '../game/squares.js';
import type { Square } from '../game/types.js';
import type { ViewerRotation } from '../render/geometry.js';

export interface KeyboardPlayCallbacks {
  readonly getRotation: () => ViewerRotation;
  /** Enter / Space on the cursor square. */
  readonly activate: (sq: Square) => void;
  readonly onCursorMove: (sq: Square) => void;
}

// Start on e2 from White's side — a natural first piece to pick up.
const INITIAL_ROW = 6;
const INITIAL_COL = 4;

/**
 * Arrow-key cursor over the board. The cursor lives in screen coordinates
 * (Arrow Up is always "up the screen"), mapped to a board square through the
 * current rotation; narration still names squares from White's side.
 */
export class KeyboardPlay {
  private readonly nav: KeyboardNavigator;
  private focused = false;
  /** The cursor is a keyboard affordance: a click or tap on the board must not draw it. */
  private byKeyboard = false;

  constructor(private readonly callbacks: KeyboardPlayCallbacks) {
    this.nav = new KeyboardNavigator({
      layout: { mode: 'grid', cols: BOARD_SIZE, rows: BOARD_SIZE },
      initialIndex: INITIAL_ROW * BOARD_SIZE + INITIAL_COL,
    });
    this.nav.onFocusChange(() => this.callbacks.onCursorMove(this.cursor()));
  }

  /** `byKeyboard`: the focus came from the keyboard (Tab), not from a click or a script. */
  setFocused(focused: boolean, byKeyboard = false): void {
    this.focused = focused;
    this.byKeyboard = focused && byKeyboard;
  }

  /** The board was clicked or touched: the player is not using the cursor. */
  pointerUsed(): void {
    this.byKeyboard = false;
  }

  /** The cursor square while the canvas is focused and being driven from the keyboard, else null. */
  focusSquare(): Square | null {
    return this.focused && this.byKeyboard ? this.cursor() : null;
  }

  cursor(): Square {
    const idx = this.nav.getIndex();
    const row = Math.floor(idx / BOARD_SIZE);
    const col = idx % BOARD_SIZE;
    return this.callbacks.getRotation() === 0 ? squareAt(col, row) : squareAt(BOARD_SIZE - 1 - col, BOARD_SIZE - 1 - row);
  }

  /** Returns true when the key was consumed. */
  handleKey(key: string): boolean {
    const hidden = !this.byKeyboard;
    const consumed = this.nav.handleKey(key);
    const activates = !consumed && (key === 'Enter' || key === ' ');
    if (!consumed && !activates) return false;
    this.byKeyboard = true;
    // The cursor keeps its square while hidden. Acting on a square the player
    // cannot see would let a stray Space play a move, so the first Enter or
    // Space only brings the cursor back (and names its square).
    if (activates && hidden) this.callbacks.onCursorMove(this.cursor());
    else if (activates) this.callbacks.activate(this.cursor());
    return true;
  }
}
